import { z } from 'zod';

import { bulkGroupCreateSchema, bulkGroupSchema } from '../bulk-group';
import { ExportKind } from '../export';
import { mediaSchema } from '../media';
import { productSchema } from '../product';
import { productSortAndFilter } from './product';

const bulkGroupSortAndFilter = z.object({
  offset: z.coerce.number().nullish(),
  limit: z.coerce.number().nullish(),
  sortBy: z.enum(['createdAt', 'updatedAt', 'title', 'author'] as const).nullish(),
  sortByDir: z.enum(['ASC', 'DESC']).nullish(),
});
export type BulkGroupSortAndFilter = z.infer<typeof bulkGroupSortAndFilter>;

//////////////////////////
// Bulk group get socket event

const bulkGroupSocketRes = z.object({
  bulkGroup: bulkGroupSchema,
});
export const getBulkGroupSocketResSchema = z.object({
  list: z.array(bulkGroupSocketRes),
  errors: z.array(
    z.object({
      bulkGroupId: z.string(),
      errorMsg: z.string(),
      errorCode: z.number(),
    })
  ),
  listDeleted: z.array(z.string()),
});
export type GetBulkGroupSocketRes = z.infer<typeof getBulkGroupSocketResSchema>;

// List
export const listReqSchema = z.object({
  params: z.object({}).nullish(),
  query: bulkGroupSortAndFilter.strip().nullish(),
  body: z.object({}).nullish(),
});
export type ListReq = z.infer<typeof listReqSchema>;

export const listResSchema = {
  200: z.object({
    data: z.object({
      bulkGroups: z.array(bulkGroupSchema),
    }),
  }),
};
export type ListRes = z.infer<(typeof listResSchema)[200]>;

//////////////////////////
// GetByBrandId
export const getBulkGroupByBrandIdReqSchema = z.object({
  params: z.object({
    id: bulkGroupSchema.shape.brandId,
  }),
  query: bulkGroupSortAndFilter.strip().nullish(),
  body: z.object({}).nullish(),
});
export type GetBulkGroupByBrandIdReq = z.infer<typeof getBulkGroupByBrandIdReqSchema>;

export const getBulkGroupByBrandIdResSchema = {
  200: z.object({
    data: z.object({
      bulkGroups: z.array(bulkGroupSchema),
    }),
  }),
};
export type GetBulkGroupByBrandIdRes = z.infer<(typeof getBulkGroupByBrandIdResSchema)[200]>;

// Get bulk group with all products and medias associated
export const getBulkGroupProductsReqSchema = z.object({
  params: z.object({
    id: bulkGroupSchema.shape._id,
  }),
  query: productSortAndFilter.strip().nullish(),
  body: z.object({}).nullish(),
});
export type GetBulkGroupProductsReq = z.infer<typeof getBulkGroupProductsReqSchema>;

export const getBulkGroupProductsResSchema = {
  200: z.object({
    data: z.object({
      bulkGroup: bulkGroupSchema.strip(),
      products: z.array(productSchema),
      medias: z.array(mediaSchema),
    }),
    totalCount: z.number(),
  }),
};
export type GetBulkGroupProductsRes = z.infer<(typeof getBulkGroupProductsResSchema)[200]>;

//////////////////////////
// Create

const fileSchema = z
  .object({
    lastModifiedDate: z.date(),
    filepath: z.string(),
    newFilename: z.string(),
    originalFilename: z.string(),
    mimetype: z.string(),
    size: z.number(),
  })
  .strip();
export const createBulkGroupReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: bulkGroupCreateSchema.strip(),
  files: z.object({
    file: z.union([fileSchema, z.array(fileSchema)]),
  }),
});
export type CreateBulkGroupReq = z.infer<typeof createBulkGroupReqSchema>;

export const createBulkGroupResSchema = {
  200: z.object({
    data: bulkGroupSchema.strip(),
  }),
};
export type CreateBulkGroupRes = z.infer<(typeof createBulkGroupResSchema)[200]>;

//////////////////////////
// Save
export const updateBulkGroupReqSchema = z.object({
  params: z.object({
    id: bulkGroupSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: bulkGroupCreateSchema.partial().strip(),
});
export type UpdateBulkGroupReq = z.infer<typeof updateBulkGroupReqSchema>;

export const updateBulkGroupResSchema = {
  200: z.object({
    data: bulkGroupSchema.strip(),
  }),
};
export type UpdateBulkGroupRes = z.infer<(typeof updateBulkGroupResSchema)[200]>;

//////////////////////////
// Export
export const exportBulkGroupReqSchema = z.object({
  params: z.object({
    id: bulkGroupSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({
    kind: z.nativeEnum(ExportKind).default(ExportKind.CSV),
  }),
});
export type ExportBulkGroupReq = z.infer<typeof exportBulkGroupReqSchema>;

export const exportBulkGroupResSchema = {
  200: z.object({
    data: z.string(),
  }),
};
export type ExportBulkGroupRes = z.infer<(typeof exportBulkGroupResSchema)[200]>;

//////////////////////////
// Delete
export const delBulkGroupReqSchema = z.object({
  params: z.object({
    id: bulkGroupSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({
    shouldRemoveProducts: z.boolean().default(false),
  }),
});
export type DelBulkGroupReq = z.infer<typeof delBulkGroupReqSchema>;

export const delBulkGroupResSchema = {
  200: z.object({
    bulkGroup: bulkGroupSchema.strip(),
    productsIdsToRemove: z.array(productSchema.shape._id),
  }),
};
export type DelBulkGroupRes = z.infer<(typeof delBulkGroupResSchema)[200]>;
